import { useQuery } from '@tanstack/react-query';
import { Element, type HTMLReactParserOptions } from 'html-react-parser';
import { ExternalLink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Empty from '@/components/Empty/Empty';
import { studioQueryKeys } from '@/configs/react-query-keys/studio';
import { usePublic } from '@/contexts/PublicContext';
import { MessageTextStyle } from '@/entities/hey-syncly';
import type { CategoryMentionListModalOptions } from '@/features/hey-syncly/ui/mention-list-modal/types';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';

import { studioService } from '../../api/service';
import type { HeySynclyStudioItem } from '../../model';
import { StudioItemMarkdown } from './studio-item-markdown';
import { StudioItemDetailHeader } from './StudioItemDetailHeader';

const CategoryIcon = ({
  category_id,
  itemId,
}: {
  category_id: number;
  itemId: string;
}) => {
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const handleClick = () => {
    setNewModal<CategoryMentionListModalOptions>({
      alias: itemId,
      type: DETAIL_MODAL_TYPE.CATEGORY_MENTION_LIST,
      options: {
        item_id: itemId,
        category_id: category_id,
      },
    });
  };

  return (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
      className="translate-y-0.5"
      outlined={false}
      icon={<ExternalLink className="size-3.5" />}
    />
  );
};

export const StudioItemDetailContainer = (props: {
  alias: Alias;
  onClose?: () => void;
}) => {
  const { alias } = props;
  const isPublic = usePublic();

  const { data, isLoading } = useQuery({
    queryKey: [studioQueryKeys.STUDIO_ITEM_DETAIL, alias, isPublic],
    queryFn: () =>
      isPublic
        ? studioService.GetHeySynclyStudioItemPublic(alias)
        : studioService.GetHeySynclyStudioItem(alias),
  });

  if (isLoading)
    return (
      <CenterFlex height="100%">
        <Spinner />
      </CenterFlex>
    );

  return (
    <Transition status>
      <Body data={data} onClose={props.onClose} />
    </Transition>
  );
};

const Body = ({
  data,
  onClose = () => {},
}: {
  data?: HeySynclyStudioItem;
  onClose?: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return;
    }

    const updateContainerWidth = () => {
      setContainerWidth(node.clientWidth);
    };

    updateContainerWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateContainerWidth);

      return () => {
        window.removeEventListener('resize', updateContainerWidth);
      };
    }

    const observer = new ResizeObserver(updateContainerWidth);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (!data)
    return <Empty title="Report not found" description="Report not found" />;

  return (
    <div
      ref={containerRef}
      className="flex flex-col h-screen min-w-0 overflow-hidden"
    >
      <StudioItemDetailHeader
        className="w-full"
        compactActions={containerWidth > 0 && containerWidth <= 600}
        item={data}
        onClose={onClose}
      />
      <div className="flex flex-col items-center flex-1 min-w-0 gap-4 px-16 overflow-y-auto py-14">
        <div className="flex flex-col w-full max-w-4xl min-w-0">
          <MarkdownRenderer content={data.content.body} itemId={data.alias} />
        </div>
      </div>
    </div>
  );
};

const MarkdownRenderer = (props: { content: string; itemId: string }) => {
  const { content, itemId } = props;

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.name === 'category') {
        const rawId = domNode.attribs.id || '';
        const cleanId = rawId.replace('/', '').trim();
        return <CategoryIcon category_id={Number(cleanId)} itemId={itemId} />;
      }
    },
  };
  return (
    <StudioDetailMessageTextStyle className="p-2 rounded-3xl">
      <StudioItemMarkdown content={content} options={options} />
    </StudioDetailMessageTextStyle>
  );
};

const StudioDetailMessageTextStyle = styled(MessageTextStyle)`
  ol li::marker {
    color: inherit;
  }
`;
